import React, { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import SimpleNextJSChart from '@/components/charts/SimpleNextJSChart'
import { ThemeToggle } from '@/components/ui/ThemeToggle'
import { apiService } from '@/services/api'
import { LoadingSpinner } from '@/components/ui/LoadingSpinner'
import { useSettingsStore } from '@/store/settingsStore'

interface KPICardProps {
  title: string
  value: string | number
  change: number
  icon: string
  color: string
  trend?: 'up' | 'down' | 'stable'
}

const KPICard: React.FC<KPICardProps> = ({ title, value, change, icon, color, trend = 'stable' }) => {
  const getTrendColor = () => {
    switch (trend) {
      case 'up': return 'text-green-400'
      case 'down': return 'text-red-400'
      default: return 'text-muted-foreground'
    }
  }

  const getTrendIcon = () => {
    switch (trend) {
      case 'up': return '↗️'
      case 'down': return '↘️'
      default: return ''
    }
  }

  return (
    <div className="glass-card hover-lift group h-full flex flex-col">
      <div className="flex items-start justify-between mb-4">
        <div className={`p-3 rounded-xl flex-shrink-0 ${color}`}>
          <span className="text-2xl">{icon}</span>
        </div>
        {change !== 0 && (
          <div className={`flex items-center space-x-1 text-sm ${getTrendColor()}`}>
            <span>{getTrendIcon()}</span>
            <span>{Math.abs(change)}%</span>
          </div>
        )}
      </div>
      <div className="flex-1 flex flex-col justify-end">
        <h3 className="text-2xl font-bold esales-text mb-1">{value}</h3>
        <p className="text-muted-foreground text-sm">{title}</p>
      </div>
    </div>
  )
}

export const DashboardPage: React.FC = () => {
  const { settings } = useSettingsStore()
  const [currentTime, setCurrentTime] = useState(new Date())
  const [stats, setStats] = useState<any>(null)
  const [aiInsights, setAiInsights] = useState<any[]>([])
  const [reorderCount, setReorderCount] = useState<number | null>(null)
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    const fetchReorderCount = async () => {
      try {
        const res = await apiService.get<{ success: boolean; data: unknown[] }>('/stats/reports/reorder')
        if (res.success && Array.isArray(res.data)) setReorderCount(res.data.length)
        else setReorderCount(0)
      } catch {
        setReorderCount(null)
      }
    }
    fetchReorderCount()
  }, [])

  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        const statsRes = await apiService.get<any>('/stats/dashboard')
        if (statsRes.success) {
          setStats(statsRes.data)
        }

        // Static fallbacks or mock AI insights for now
        setAiInsights([
          {
            title: 'Revenue Optimization',
            insight: 'Your weekend sales are 40% higher than weekdays. Consider extending weekend hours.',
            confidence: 92,
            type: 'success'
          },
          {
            title: 'Inventory Alert',
            insight: '"Clean Code" stock is running low. Reorder within 3 days to avoid stockout.',
            confidence: 88,
            type: 'warning'
          }
        ])
      } catch (error: any) {
        const isTimeout = error?.code === 'ECONNABORTED' || error?.message?.includes('timeout')
        if (isTimeout) {
          console.warn('Dashboard: request timed out. If using local dev, ensure the backend is running (e.g. port 5000).', error?.config?.baseURL)
        } else {
          console.error('Failed to fetch dashboard data:', error)
        }
      } finally {
        setIsLoading(false)
      }
    }

    fetchDashboardData()
    // Increase interval to 5 minutes to avoid rate limiting
    const interval = setInterval(fetchDashboardData, 300000)
    return () => clearInterval(interval)
  }, [])

  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 1000)
    return () => clearInterval(timer)
  }, [])

  if (isLoading && !stats) {
    return (
      <LoadingSpinner 
        variant="fullscreen"
        size="lg"
        message="Syncing Live Data"
        submessage="Connecting to your business dashboard..."
      />
    )
  }

  // Merge stats.kpis with defaults to ensure all fields exist
  const defaultKpis = { 
    totalRevenue: 0, 
    totalTransactions: 0, 
    totalCustomers: 0, 
    avgOrderValue: 0,
    activeEmployees: 0,
    inventoryWorth: 0,
    lowStockCount: 0,
    outOfStockCount: 0,
    totalProducts: 0
  }
  const kpis = { ...defaultKpis, ...(stats?.kpis || {}) }
  const charts = stats?.charts || { revenue: [], sales: [], expenses: [], billPayments: [] }
  const recentActivities = stats?.recentActivities || []
  const billPaymentsToday = kpis.billPaymentsToday || { count: 0, total: 0, commissionEarned: 0 }
  const expensesToday = kpis.expensesToday || { count: 0, total: 0 }

  // Format inventory worth for display
  const formatCurrency = (value: number | undefined | null) => {
    const safeValue = value ?? 0
    if (safeValue >= 1000000) return `Rs.${(safeValue / 1000000).toFixed(1)}M`
    if (safeValue >= 1000) return `Rs.${(safeValue / 1000).toFixed(1)}k`
    return `Rs.${safeValue.toFixed(0)}`
  }

  const kpiCardsData = [
    { 
      title: 'Total Revenue', 
      value: `Rs.${(kpis.totalRevenue ?? 0).toLocaleString()}`, 
      change: 0, 
      icon: '💰', 
      color: 'bg-emerald-500/15 text-emerald-300 border border-emerald-500/30', 
      trend: 'stable' as const 
    },
    { 
      title: 'Total Orders', 
      value: kpis.totalTransactions ?? 0, 
      change: 0, 
      icon: '🛒', 
      color: 'bg-sky-500/15 text-sky-300 border border-sky-500/30', 
      trend: 'stable' as const 
    },
    { 
      title: 'Avg Order Value', 
      value: `Rs.${(kpis.avgOrderValue ?? 0).toFixed(2)}`, 
      change: 0, 
      icon: '📊', 
      color: 'bg-amber-500/15 text-amber-300 border border-amber-500/30', 
      trend: 'stable' as const 
    },
    { 
      title: 'Total Customers', 
      value: kpis.totalCustomers ?? 0, 
      change: 0, 
      icon: '👥', 
      color: 'bg-pink-500/15 text-pink-300 border border-pink-500/30', 
      trend: 'stable' as const 
    },
    { 
      title: 'Active Employees', 
      value: kpis.activeEmployees ?? 0, 
      change: 0, 
      icon: '👤', 
      color: 'bg-violet-500/15 text-violet-300 border border-violet-500/30', 
      trend: 'stable' as const 
    },
    { 
      title: 'Inventory Worth', 
      value: formatCurrency(kpis.inventoryWorth), 
      change: 0, 
      icon: '📦', 
      color: 'bg-orange-500/15 text-orange-300 border border-orange-500/30', 
      trend: 'stable' as const 
    }
  ]

  return (
    <div className="min-h-screen bg-gradient-space p-3 sm:p-4 lg:p-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 mb-6 sm:mb-8">
        <div>
          <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold esales-text font-display">{settings.storeName} Dashboard</h1>
          <p className="text-muted-foreground mt-2">Dynamic real-time insights for your bookstore.</p>
          <div className="flex items-center mt-2 space-x-4">
            <div className="flex items-center space-x-2">
              <div className="w-2 h-2 rounded-full bg-green-400 animate-pulse"></div>
              <span className="text-sm text-muted-foreground">Live Data Sync</span>
            </div>
            <div className="text-sm text-muted-foreground">{currentTime.toLocaleTimeString()}</div>
          </div>
        </div>
        <ThemeToggle />
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4 mb-4">
        {kpiCardsData.map((kpi, idx) => (
          <KPICard key={idx} {...kpi} />
        ))}
      </div>

      {/* Bill Payments & Expenses KPIs */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
        <div className="glass-card hover-lift">
          <div className="flex items-start justify-between mb-2">
            <div className="p-3 rounded-xl bg-blue-500/15 text-blue-300 border border-blue-500/30">
              <span className="text-2xl">🧾</span>
            </div>
          </div>
          <h3 className="text-2xl font-bold esales-text mb-1">
            {billPaymentsToday.count} <span className="text-base font-normal text-muted-foreground">/ Rs.{(billPaymentsToday.total || 0).toLocaleString()}</span>
          </h3>
          <p className="text-muted-foreground text-sm">Bill Payments Today</p>
        </div>
        <div className="glass-card hover-lift">
          <div className="flex items-start justify-between mb-2">
            <div className="p-3 rounded-xl bg-green-500/15 text-green-300 border border-green-500/30">
              <span className="text-2xl">💸</span>
            </div>
          </div>
          <h3 className="text-2xl font-bold esales-text mb-1">Rs.{(billPaymentsToday.commissionEarned || 0).toLocaleString()}</h3>
          <p className="text-muted-foreground text-sm">Commission Earned Today</p>
        </div>
        <div className="glass-card hover-lift">
          <div className="flex items-start justify-between mb-2">
            <div className="p-3 rounded-xl bg-red-500/15 text-red-300 border border-red-500/30">
              <span className="text-2xl">📋</span>
            </div>
          </div>
          <h3 className="text-2xl font-bold esales-text mb-1">
            {expensesToday.count} <span className="text-base font-normal text-muted-foreground">/ Rs.{(expensesToday.total || 0).toLocaleString()}</span>
          </h3>
          <p className="text-muted-foreground text-sm">Expenses Today</p>
        </div>
      </div>

      {reorderCount != null && reorderCount > 0 && (
        <div className="mb-6">
          <Link
            to="/reports"
            className="block p-4 rounded-xl border border-amber-500/30 bg-amber-500/10 hover:bg-amber-500/20 transition-colors"
          >
            <div className="flex items-center justify-between">
              <span className="font-semibold text-amber-600 dark:text-amber-400">Below reorder level</span>
              <span className="font-bold text-amber-600 dark:text-amber-400">{reorderCount} product{reorderCount !== 1 ? 's' : ''}</span>
            </div>
            <p className="text-xs text-muted-foreground mt-1">View Reorder report</p>
          </Link>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
        <div className="glass-card">
          <h3 className="text-xl font-bold esales-text mb-4">Revenue Trend</h3>
          <div className="h-84">
            <SimpleNextJSChart
              data={charts.revenue.map((d: any) => ({ x: d.date, y: d.value, label: d.label }))}
              color="var(--primary)"
              type="line"
            />
          </div>
        </div>
        <div className="glass-card">
          <h3 className="text-xl font-bold esales-text mb-4">Weekly Sales</h3>
          <div className="h-84">
            <SimpleNextJSChart
              data={charts.sales.map((d: any) => ({ x: d.date, y: d.value, label: d.label }))}
              color="var(--secondary)"
              type="bar"
            />
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2">
          <div className="glass-card">
            <h3 className="text-xl font-semibold text-foreground mb-6">Recent Transactions</h3>
            <div className="space-y-4">
              {recentActivities.length === 0 ? (
                <p className="text-muted-foreground text-center py-10">No recent transactions found.</p>
              ) : (
                recentActivities.map((activity: any) => (
                  <div key={activity.id} className="flex items-center space-x-4 p-3 rounded-lg hover:bg-muted/20 transition-colors">
                    <div className="text-2xl">{activity.icon}</div>
                    <div className="flex-1">
                      <p className="text-sm font-medium text-foreground">{activity.title}</p>
                      <p className="text-xs text-muted-foreground">{activity.description}</p>
                    </div>
                    <div className="text-xs text-muted-foreground">
                      {new Date(activity.time).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>

        <div className="glass-card">
          <div className="flex items-center space-x-2 mb-4">
            <span className="text-2xl">🧠</span>
            <h3 className="text-lg font-semibold text-foreground">AI Business Insights</h3>
          </div>
          <div className="space-y-4">
            {aiInsights.map((insight, idx) => (
              <div key={idx} className="p-4 rounded-lg bg-background/50 border border-border/50">
                <div className="flex justify-between items-start mb-2">
                  <h4 className="text-sm font-bold text-foreground">{insight.title}</h4>
                  <span className="text-[10px] px-2 py-0.5 rounded-full bg-primary/20 text-primary">{insight.confidence}% confidence</span>
                </div>
                <p className="text-xs text-muted-foreground">{insight.insight}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  )
}